import React, { useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@material-ui/core";
import { useHistory } from "react-router-dom";

import { h5Medium, h6Medium, h7Regular, h3 } from "../../../shared/typography";
import { pointer } from "../../../shared/mixins";
import { ReactComponent as CreditCardIcon } from "../../../assets/credit-card.svg";
import { ReactComponent as PlusIcon } from "../../../assets/plus.svg";
import { useTranslation } from "react-i18next";
import { useSavedPaymentMethods } from "./hooks/useSavedPaymentMethods";
import SavedPaymentMethods from "./SavedPaymentMethods/SavedPaymentMethods";
import InstallmentsSelector from "./InstallmentsSelector/InstallmentsSelector";

interface PaymentProcessProcesses {
  stage3DeliverTo: boolean;
  stage4Payment: boolean;
}

interface PaymentProcessProps {
  setProcesses: () => void;
  processes: PaymentProcessProcesses;
  onEditPayment: () => void;
  onPaymentTokenSelected: (id: string | null) => void;
}

const useStyles = makeStyles({
  headerTitle: {
    ...h3,
  },

  editLink: {
    ...h6Medium,
    color: "#80BB34",
  },

  subText: {
    ...h7Regular,
    opacity: 0.64,
    display: "block",
    marginBottom: 24,
  },

  addNewCard: {
    ...h6Medium,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#80BB34",
    marginBottom: 24,
    ...pointer,
  },

  button: {
    height: 56,
    width: 240,
    border: "none",
    textAlign: "center",
    borderRadius: 16,
    marginTop: 24,
    ...h5Medium,
    color: "#FFFFFF",
    ...pointer,
  },
});

const PaymentProcess: React.FC<PaymentProcessProps> = ({
  setProcesses,
  processes,
  onEditPayment,
  onPaymentTokenSelected,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const { stage3DeliverTo, stage4Payment } = processes;

  const [closeAccordion, setCloseAccordion] = useState(false);

  useEffect(() => {
    setCloseAccordion(stage3DeliverTo);
  }, [stage3DeliverTo]);

  const {
    paymentTokens,
    hasChecked,
    selected,
    setSelected,
    wantsInstallments,
    setWantsInstallments,
    installmentsCount,
    setInstallmentsCount,
    isValidContinue,
  } = useSavedPaymentMethods(stage3DeliverTo);

  const goToBankCards = () => {
    history.push("/account/bank-cards");
  };

  // No saved cards -> nothing to choose here, so auto-advance past this
  // stage instead of leaving later steps (e.g. delivery) permanently blocked.
  useEffect(() => {
    if (hasChecked && paymentTokens.length === 0 && !stage4Payment) {
      setProcesses();
    }
  }, [hasChecked, paymentTokens.length, stage4Payment, setProcesses]);

  // Keep the chosen card's id synced up to Checkout so the final
  // "Confirm order" action knows which saved token to charge.
  useEffect(() => {
    onPaymentTokenSelected(selected?.id ?? null);
  }, [selected, onPaymentTokenSelected]);

  // Default to not showing this step at all — only render once we've
  // actually confirmed there are saved cards to choose from.
  if (!hasChecked || paymentTokens.length === 0) {
    return null;
  }

  return (
    <Accordion
      style={{
        borderRadius: "16px",
        padding: 30,
        boxShadow: "0px 16px 32px rgba(29, 38, 102, 0.08)",
        marginBottom: 24,
        borderBottom: "none",
        background: "#FFF",
      }}
      expanded={closeAccordion}
      disabled={!stage3DeliverTo}
    >
      <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
        <Typography style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                opacity: stage3DeliverTo ? 1 : 0.64,
              }}
            >
              <CreditCardIcon style={{ marginRight: 16 }} />
              <span className={classes.headerTitle}>
                {t("checkout.Payment Method")}
              </span>
            </div>
            {stage4Payment ? (
              <span
                className={classes.editLink}
                onClick={() => {
                  setCloseAccordion(true);
                  onEditPayment();
                }}
              >
                {t("common.edit")}
              </span>
            ) : (
              ""
            )}
          </div>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: "100%" }}>
          <span className={classes.subText}>
            {t("checkout.estimatedAmountBlocked")}
          </span>

          <SavedPaymentMethods
            paymentTokens={paymentTokens}
            selected={selected}
            onSelect={setSelected}
          />

          <span className={classes.addNewCard} onClick={goToBankCards}>
            <PlusIcon />
            {t("checkout.addNewCard")}
          </span>

          <InstallmentsSelector
            wantsInstallments={wantsInstallments}
            setWantsInstallments={setWantsInstallments}
            installmentsCount={installmentsCount}
            setInstallmentsCount={setInstallmentsCount}
          />

          <button
            className={classes.button}
            style={{
              background: isValidContinue ? "#80BB34" : "lightgray",
            }}
            disabled={!isValidContinue}
            onClick={() => {
              setProcesses();
              setCloseAccordion(false);
            }}
          >
            {t("common.Continue")}
          </button>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default PaymentProcess;
